import { useStore, MiniMapNodeProps } from '@xyflow/react'
import { ComponentType, memo, useState } from 'react'
import { shallow } from 'zustand/shallow'
import './FlowFullMap.css'

interface MiniMapNodesProps {
  nodeStrokeWidth?: number
  nodeComponent: ComponentType<any>
  nodeColor: any
  onClick?: (nodeId: string) => void
  containerWidth: number
  containerHeight: number
  contentBounds: {
    minX: number
    minY: number
    maxX: number
    maxY: number
  } | null
  clickedNodeId?: string | null
}

function MiniMapNodes({
  nodeStrokeWidth,
  nodeComponent: NodeComponent,
  nodeColor,
  onClick,
  containerWidth,
  containerHeight,
  contentBounds,
  clickedNodeId,
}: MiniMapNodesProps) {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)

  const nodes = useStore((s) => {
    const nodeArray = Array.from(s.nodeLookup.values())

    return nodeArray.map((node) => {
      const { x, y } = node.internals?.positionAbsolute || { x: 0, y: 0 }
      const width = node.width || 100
      const height = node.height || 50

      return {
        id: node.id,
        position: { x, y },
        width,
        height,
        data: node.data,
        type: node.type,
      }
    })
  }, shallow)

  if (!nodes || nodes.length === 0) {
    return null
  }

  // Separate hovered and non-hovered nodes for proper layering
  const regularNodes = nodes.filter((node) => hoveredNodeId !== node.id)
  const hoveredNode = nodes.find((node) => hoveredNodeId === node.id)

  return (
    <>
      {/* Render regular nodes first */}
      {regularNodes.map((node) => (
        <NodeComponent
          key={node.id}
          id={node.id}
          x={node.position.x}
          y={node.position.y}
          width={node.width}
          height={node.height}
          color={typeof nodeColor === 'function' ? nodeColor(node) : nodeColor}
          strokeWidth={nodeStrokeWidth}
          onClick={onClick}
          data={node.data}
          type={node.type}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          contentBounds={contentBounds}
          isHovered={false}
          onHover={(id: string | null) => setHoveredNodeId(id)}
          isClicked={clickedNodeId === node.id}
        />
      ))}

      {/* Render hovered node last so it appears on top */}
      {hoveredNode && (
        <NodeComponent
          key={hoveredNode.id}
          id={hoveredNode.id}
          x={hoveredNode.position.x}
          y={hoveredNode.position.y}
          width={hoveredNode.width}
          height={hoveredNode.height}
          color={
            typeof nodeColor === 'function' ? nodeColor(hoveredNode) : nodeColor
          }
          strokeWidth={nodeStrokeWidth}
          onClick={onClick}
          data={hoveredNode.data}
          type={hoveredNode.type}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          contentBounds={contentBounds}
          isHovered={true}
          onHover={(id: string | null) => setHoveredNodeId(id)}
          isClicked={clickedNodeId === hoveredNode.id}
        />
      )}
    </>
  )
}

const MiniMapNode: React.FC<MiniMapNodeProps> = memo(
  ({
    x,
    y,
    width,
    height,
    style,
    color,
    strokeColor,
    strokeWidth,
    className,
    ...nodeData
  }) => {
    const { data, type } = nodeData as any

    // Get minified info from node data - same pattern as NodeBaseContainer
    const minifiedInfo = data?.minifiedInfo || {}
    const minifiedEntries = Object.entries(minifiedInfo).filter(
      ([key, value]) =>
        value && typeof value === 'string' && value.trim().length > 0
    )

    return (
      <g>
        {/* Node background */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          className={className}
          style={style}
        />

        {/* Node type and label */}
        <text
          x={x + 8}
          y={y + 16}
          fontSize="10"
          fontWeight="bold"
          fill="#374151"
          className="minimap-node-title"
        >
          {type?.toUpperCase() || 'NODE'}
        </text>

        <text
          x={x + 8}
          y={y + 28}
          fontSize="9"
          fill="#333"
          className="minimap-node-label"
        >
          {data?.label || 'Untitled'}
        </text>

        {/* Minified info - following NodeBaseContainer pattern */}
        {minifiedEntries.length > 0 && (
          <g className="minimap-minified-info">
            {minifiedEntries.slice(0, 3).map(([key, value], index) => (
              <g key={key}>
                {/* Key/label */}
                <text
                  x={x + 8}
                  y={y + 44 + index * 24}
                  fontSize="7"
                  fontWeight="600"
                  fill="#6b7280"
                  className="minimap-info-label"
                >
                  {key}:
                </text>
                {/* Value */}
                <text
                  x={x + 8}
                  y={y + 54 + index * 24}
                  fontSize="7"
                  fill="#9ca3af"
                  className="minimap-info-value"
                >
                  {String(value).substring(0, 25)}
                  {String(value).length > 25 ? '...' : ''}
                </text>
              </g>
            ))}
          </g>
        )}
      </g>
    )
  }
)

export default memo(MiniMapNodes)
